<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showScheduleMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['setMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');
const wootEditorScheduleMode = useTemplateRef('wootEditorScheduleMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);
const scheduleModeSize = useElementSize(wootEditorScheduleMode);

/**
 * Computed to get the current active mode
 * When disabled, always show NOTE mode regardless of actual mode prop
 */
const activeMode = computed(() => {
  if (props.disabled) return REPLY_EDITOR_MODES.NOTE;
  return props.mode;
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 */
const width = computed(() => {
  let widthToUse = replyModeSize.width.value;

  if (activeMode.value === REPLY_EDITOR_MODES.NOTE) {
    widthToUse = privateModeSize.width.value;
  } else if (activeMode.value === REPLY_EDITOR_MODES.SCHEDULE) {
    widthToUse = scheduleModeSize.width.value;
  }

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 */
const translateValue = computed(() => {
  let xTranslate = 0;

  if (activeMode.value === REPLY_EDITOR_MODES.NOTE) {
    xTranslate = replyModeSize.width.value + 16;
  } else if (activeMode.value === REPLY_EDITOR_MODES.SCHEDULE) {
    xTranslate = replyModeSize.width.value + privateModeSize.width.value + 32;
  }

  return `${xTranslate}px`;
});

const handleClick = event => {
  const target = event.target.closest('[data-mode]');
  if (!target) return;

  const mode = target.dataset.mode;
  if (props.disabled && mode === REPLY_EDITOR_MODES.REPLY) return;
  emit('setMode', mode);
};
</script>

<template>
  <button
    class="flex items-center w-auto h-8 p-1 transition-all rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0 active:scale-[0.995] active:duration-75"
    :disabled="disabled"
    :class="{
      'cursor-not-allowed': disabled,
    }"
    @click="handleClick"
  >
    <div
      ref="wootEditorReplyMode"
      :data-mode="REPLY_EDITOR_MODES.REPLY"
      class="flex items-center gap-1 px-2 z-20"
      :class="{
        'opacity-50': disabled,
      }"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </div>
    <div
      ref="wootEditorPrivateMode"
      :data-mode="REPLY_EDITOR_MODES.NOTE"
      class="flex items-center gap-1 px-2 z-20"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </div>
    <div
      v-if="showScheduleMode"
      ref="wootEditorScheduleMode"
      :data-mode="REPLY_EDITOR_MODES.SCHEDULE"
      class="flex items-center gap-1 px-2 z-20"
    >
      {{ $t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.MODE_LABEL') }}
    </div>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :class="{
        'transition-all duration-300': !disabled,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
